import { useState, useEffect, useMemo } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Search,
  Copy,
  Database,
  RefreshCw,
  Trash2,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "react-hot-toast";

// Known app storage keys
const APP_STORAGE_KEYS = [
  "auth-storage",
  "dashboard-store",
  "jp-theme",
  "credit-store",
  "agent-store",
  "justping_user",
  "justping_token",
  "justping_refresh_token",
  "justping_theme",
  "justping_onboarding",
  "justping_preferences",
  "justping_library",
  "justping_debug_tool",
];

interface StorageItem {
  key: string;
  value: string;
  size: number;
  isAppKey: boolean;
  parsed: unknown;
  parseError?: string;
}

export function StorageDebugTab() {
  const [storageItems, setStorageItems] = useState<StorageItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
    new Set(["auth-storage"])
  );
  const [showOnlyAppKeys, setShowOnlyAppKeys] = useState(true);

  // Load storage data
  const loadStorageData = () => {
    const items: StorageItem[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key) || "";
        let parsed: unknown = value;
        let parseError: string | undefined;

        try {
          parsed = JSON.parse(value);
        } catch {
          parseError = "Not valid JSON";
        }

        items.push({
          key,
          value,
          size: new Blob([value]).size,
          isAppKey: APP_STORAGE_KEYS.some(
            (appKey) => key.includes(appKey) || key.startsWith("justping")
          ),
          parsed,
          parseError,
        });
      }
    }

    // Sort: app keys first, then alphabetically
    items.sort((a, b) => {
      if (a.isAppKey && !b.isAppKey) return -1;
      if (!a.isAppKey && b.isAppKey) return 1;
      return a.key.localeCompare(b.key);
    });

    setStorageItems(items);
  };

  useEffect(() => {
    loadStorageData();
  }, []);

  // Filter items
  const filteredItems = useMemo(() => {
    let items = storageItems;

    if (showOnlyAppKeys) {
      items = items.filter((item) => item.isAppKey);
    }

    if (searchQuery) {
      items = items.filter(
        (item) =>
          item.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.value.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return items;
  }, [storageItems, searchQuery, showOnlyAppKeys]);

  // Total storage used
  const totalSize = useMemo(() => {
    return storageItems.reduce((sum, item) => sum + item.size, 0);
  }, [storageItems]);

  const appSize = useMemo(() => {
    return storageItems
      .filter((item) => item.isAppKey)
      .reduce((sum, item) => sum + item.size, 0);
  }, [storageItems]);

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const copyToClipboard = (data: unknown, label: string) => {
    const text =
      typeof data === "string" ? data : JSON.stringify(data, null, 2);
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const clearStorageKey = (key: string) => {
    if (
      confirm(
        `Are you sure you want to delete "${key}" from localStorage? This may affect app functionality.`
      )
    ) {
      localStorage.removeItem(key);
      loadStorageData();
      toast.success(`Removed ${key} from localStorage`);
    }
  };

  // Extract token expiration from auth-storage if available
  const getTokenInfo = () => {
    const authItem = storageItems.find((item) => item.key === "auth-storage");
    if (authItem && typeof authItem.parsed === "object" && authItem.parsed) {
      const state = (authItem.parsed as { state?: { token?: string } }).state;
      if (state?.token) {
        try {
          const parts = state.token.split(".");
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]));
            return {
              exp: payload.exp
                ? new Date(payload.exp * 1000).toLocaleString()
                : "Unknown",
              iat: payload.iat
                ? new Date(payload.iat * 1000).toLocaleString()
                : "Unknown",
              isExpired: payload.exp ? payload.exp * 1000 < Date.now() : false,
            };
          }
        } catch {
          return null;
        }
      }
    }
    return null;
  };

  const tokenInfo = getTokenInfo();

  return (
    <ScrollArea className="h-[calc(80vh-180px)]">
      <div className="space-y-4 pr-4">
        {/* Storage Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-muted/50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold">{storageItems.length}</div>
            <div className="text-xs text-muted-foreground">Total Keys</div>
          </div>
          <div className="bg-muted/50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold">{formatSize(totalSize)}</div>
            <div className="text-xs text-muted-foreground">Total Size</div>
          </div>
          <div className="bg-primary/10 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold">{formatSize(appSize)}</div>
            <div className="text-xs text-muted-foreground">App Data</div>
          </div>
        </div>

        {/* Token Info */}
        {tokenInfo && (
          <div
            className={cn(
              "border rounded-lg p-3",
              tokenInfo.isExpired
                ? "border-destructive/50 bg-destructive/5"
                : "border-green-500/50 bg-green-500/5"
            )}
          >
            <div className="flex items-center gap-2 mb-2">
              {tokenInfo.isExpired ? (
                <AlertTriangle className="h-4 w-4 text-destructive" />
              ) : (
                <Database className="h-4 w-4 text-green-600" />
              )}
              <span className="font-medium text-sm">JWT Token Info</span>
              <Badge variant={tokenInfo.isExpired ? "destructive" : "default"}>
                {tokenInfo.isExpired ? "Expired" : "Valid"}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="text-muted-foreground">Issued At:</div>
              <div>{tokenInfo.iat}</div>
              <div className="text-muted-foreground">Expires At:</div>
              <div
                className={cn(tokenInfo.isExpired && "text-destructive font-medium")}
              >
                {tokenInfo.exp}
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search keys or values..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
          <Button
            variant={showOnlyAppKeys ? "default" : "outline"}
            size="sm"
            onClick={() => setShowOnlyAppKeys(!showOnlyAppKeys)}
          >
            App Only
          </Button>
          <Button variant="outline" size="sm" onClick={loadStorageData}>
            <RefreshCw className="h-3 w-3" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const data: Record<string, unknown> = {};
              filteredItems.forEach((item) => {
                data[item.key] = item.parsed;
              });
              copyToClipboard(data, "All storage");
            }}
          >
            <Copy className="h-3 w-3 mr-1" />
            All
          </Button>
        </div>

        {/* Storage Items */}
        <div className="space-y-2">
          {filteredItems.length === 0 ? (
            <div className="text-center text-muted-foreground py-8 text-sm">
              No storage items found
            </div>
          ) : (
            filteredItems.map((item) => (
              <div
                key={item.key}
                className={cn(
                  "border rounded-lg overflow-hidden",
                  item.isAppKey
                    ? "border-primary/30"
                    : "border-border/50 opacity-70"
                )}
              >
                {/* Header */}
                <div
                  className={cn(
                    "flex items-center justify-between p-2 cursor-pointer",
                    item.isAppKey ? "bg-primary/5" : "bg-muted/30"
                  )}
                  onClick={() => toggleExpanded(item.key)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {expandedKeys.has(item.key) ? (
                      <ChevronDown className="h-4 w-4 shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0" />
                    )}
                    <Database className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="font-mono text-xs truncate">
                      {item.key}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className="text-xs">
                      {formatSize(item.size)}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(item.parsed, item.key);
                      }}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearStorageKey(item.key);
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedKeys.has(item.key) && (
                  <div className="p-2 border-t bg-background/50">
                    {item.parseError ? (
                      <div className="text-xs">
                        <span className="text-muted-foreground">
                          {item.parseError}:{" "}
                        </span>
                        <span className="font-mono break-all">{item.value}</span>
                      </div>
                    ) : (
                      <pre className="text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all max-h-[300px] overflow-y-auto">
                        {JSON.stringify(item.parsed, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </ScrollArea>
  );
}
